/**
 * Fotos clientseitig verarbeiten (Plan 6.3, Mieterportal 2.0): verkleinern (max. 1200 px), quadratisch zuschneiden,
 * drehen, Helligkeit anpassen und bewerten (zu dunkel / unscharf) – alles im Browser, ohne externe Dienste.
 */
export async function fotoVerkleinern(datei: File | Blob, maxKante = 1200): Promise<Blob> {
  const bitmap = await ladeBild(datei);
  const faktor = Math.min(1, maxKante / Math.max(bitmap.width, bitmap.height));
  const canvas = zeichne(bitmap, Math.round(bitmap.width * faktor), Math.round(bitmap.height * faktor));
  return alsJpeg(canvas);
}

export interface Bearbeitung {
  /** Quadratisch zuschneiden (mittig, mit Verschiebung −1…1). */
  quadrat?: boolean;
  versatzX?: number;
  versatzY?: number;
  /** 0, 90, 180, 270 */
  drehung?: number;
  /** 0.6 … 1.6, 1 = unverändert */
  helligkeit?: number;
}

/** Zuschneiden/Drehen/Helligkeit auf ein Bild anwenden; Ergebnis max. 1200 px JPEG. */
export async function fotoBearbeiten(quelle: Blob, b: Bearbeitung, maxKante = 1200): Promise<Blob> {
  const bitmap = await ladeBild(quelle);
  const dreh = ((b.drehung ?? 0) % 360 + 360) % 360;
  const quer = dreh === 90 || dreh === 270;
  const bw = quer ? bitmap.height : bitmap.width;
  const bh = quer ? bitmap.width : bitmap.height;
  // 1) drehen in ein Zwischen-Canvas
  const gedreht = document.createElement("canvas");
  gedreht.width = bw;
  gedreht.height = bh;
  const g = gedreht.getContext("2d")!;
  g.translate(bw / 2, bh / 2);
  g.rotate((dreh * Math.PI) / 180);
  g.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  if ("close" in bitmap) (bitmap as ImageBitmap).close();
  // 2) Ausschnitt
  let sx = 0, sy = 0, sw = bw, sh = bh;
  if (b.quadrat) {
    const k = Math.min(bw, bh);
    sw = sh = k;
    sx = Math.round(((bw - k) / 2) * (1 + (b.versatzX ?? 0)));
    sy = Math.round(((bh - k) / 2) * (1 + (b.versatzY ?? 0)));
  }
  const faktor = Math.min(1, maxKante / Math.max(sw, sh));
  const out = document.createElement("canvas");
  out.width = Math.round(sw * faktor);
  out.height = Math.round(sh * faktor);
  const o = out.getContext("2d")!;
  if (b.helligkeit && b.helligkeit !== 1) o.filter = `brightness(${b.helligkeit})`;
  o.drawImage(gedreht, sx, sy, sw, sh, 0, 0, out.width, out.height);
  return alsJpeg(out);
}

/** Einfache Bewertung: mittlere Helligkeit (0–255) und Schärfe (Laplace-Varianz) auf einem 160-px-Abbild. */
export async function fotoBewerten(quelle: Blob): Promise<{ dunkel: boolean; unscharf: boolean; helligkeit: number; schaerfe: number }> {
  const bitmap = await ladeBild(quelle);
  const c = zeichne(bitmap, 160, Math.max(1, Math.round((160 * bitmap.height) / bitmap.width)));
  const ctx = c.getContext("2d")!;
  const { data, width, height } = ctx.getImageData(0, 0, c.width, c.height);
  const grau = new Float32Array(width * height);
  let summe = 0;
  for (let i = 0; i < width * height; i++) {
    const v = 0.299 * data[i * 4]! + 0.587 * data[i * 4 + 1]! + 0.114 * data[i * 4 + 2]!;
    grau[i] = v;
    summe += v;
  }
  const helligkeit = summe / (width * height);
  let lap = 0, lap2 = 0, n = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const l = -4 * grau[i]! + grau[i - 1]! + grau[i + 1]! + grau[i - width]! + grau[i + width]!;
      lap += l;
      lap2 += l * l;
      n++;
    }
  }
  const varianz = n ? lap2 / n - (lap / n) ** 2 : 0;
  return { dunkel: helligkeit < 60, unscharf: varianz < 25, helligkeit: Math.round(helligkeit), schaerfe: Math.round(varianz) };
}

function zeichne(bitmap: ImageBitmap | HTMLImageElement, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Bild kann nicht verarbeitet werden.");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas;
}

async function alsJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/jpeg", 0.82));
  if (!blob) throw new Error("Bild kann nicht verarbeitet werden.");
  return blob;
}

async function ladeBild(datei: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(datei, { imageOrientation: "from-image" });
    } catch {
      // Fallback unten
    }
  }
  return new Promise((ok, fehler) => {
    const img = new Image();
    img.onload = () => ok(img);
    img.onerror = () => fehler(new Error("Bild kann nicht gelesen werden."));
    img.src = URL.createObjectURL(datei);
  });
}
